import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import React, { useState } from 'react';

import { Phone01Icon } from '@/assets/icons/Phone01Icon';

interface MeetTerriDialogProps {
  open: boolean;
  onClose: () => void;
  onCall: (phoneNumber: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

const MeetTerriDialog: React.FC<MeetTerriDialogProps> = ({
  open,
  onClose,
  onCall,
  isLoading = false,
  error = null,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } if (digits.length <= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } 
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setPhoneError('');
  };

  const validatePhoneNumber = () => {
    // Remove all non-digit characters for validation
    const digits = phoneNumber.replace(/\D/g, '');
    
    if (!digits) {
      setPhoneError('Please enter your phone number');
      return false;
    }
    
    if (digits.length !== 10) {
      setPhoneError('Please enter a valid 10-digit phone number');
      return false;
    }
    
    return true;
  };

  const handleCall = async () => {
    if (!validatePhoneNumber()) {
      return;
    }

    // Convert to E.164 format (+1XXXXXXXXXX)
    const digits = phoneNumber.replace(/\D/g, '');
    const e164Number = `+1${digits}`;
    
    await onCall(e164Number);
  };

  const handleClose = () => {
    if (!isLoading) {
      setPhoneNumber('');
      setPhoneError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1.5rem',
          color: 'secondary.900',
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Phone01Icon width={24} height={24} />
        Meet Terri - Your AI Home Assistant
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Terri will call you to understand your home maintenance needs and help you find
            the right vendors. Enter your phone number below to get started.
          </Typography>

          <TextField
            fullWidth
            label="Phone Number"
            placeholder="(555) 123-4567"
            value={phoneNumber}
            onChange={handlePhoneChange}
            error={!!phoneError}
            helperText={phoneError}
            disabled={isLoading}
            inputProps={{
              maxLength: 14, // (XXX) XXX-XXXX format
              inputMode: 'tel',
              autoComplete: 'tel',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            By providing your phone number, you agree to receive a call from Terri AI.
            Standard rates may apply.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCall}
          disabled={isLoading || !phoneNumber}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              <Phone01Icon width={20} height={20} />
            )
          }
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          {isLoading ? 'Connecting...' : 'Call Me'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetTerriDialog;